import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Clock, CheckCircle, Loader2, Info, Download, X, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [step, setStep] = useState('intro'); // intro, form, chat
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionId, setQuestionId] = useState(null);
  const [questionData, setQuestionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showIOSBanner, setShowIOSBanner] = useState(false);
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [questionData]);

  // PWA install prompt handler
  useEffect(() => {
    const handleBeforeInstallPrompt = () => {
      if (!window.deferredInstallPrompt || localStorage.getItem('install-dismissed')) return;
      setInstallPrompt(window.deferredInstallPrompt);
      setShowInstallBanner(true);
    };

    handleBeforeInstallPrompt();
    window.addEventListener('pwa-install-available', handleBeforeInstallPrompt);

    const ua = window.navigator.userAgent;
    const isIOS = /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isIOS && !isStandalone && !localStorage.getItem('ios-install-dismissed')) {
      setShowIOSBanner(true);
    }
    
    return () => {
      window.removeEventListener('pwa-install-available', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('ProcureAsk installed successfully!');
    }
    window.deferredInstallPrompt = null;
    setInstallPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismissInstall = () => {
    localStorage.setItem('install-dismissed', '1');
    setShowInstallBanner(false);
  };

  const handleDismissIOS = () => {
    localStorage.setItem('ios-install-dismissed', '1');
    setShowIOSBanner(false);
  };

  // Poll for question status updates
  useEffect(() => {
    if (!questionId) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/questions/${questionId}`);
        setQuestionData(response.data);
        
        // Stop polling if answered
        if (response.data.status === 'answered') {
          clearInterval(pollInterval);
          toast.success('Your answer is ready!');
        }
      } catch (error) {
        console.error('Error polling question status:', error);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [questionId]);

  const handleStartChat = () => {
    setStep('form');
  };

  const handleSubmitForm = () => {
    if (!userName.trim() || !userEmail.trim()) {
      toast.error('Please provide your name and email');
      return;
    }
    setStep('chat');
  };

  const handleSubmitQuestion = async () => {
    if (!currentQuestion.trim()) {
      toast.error('Please enter a question');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/questions`, {
        user_name: userName,
        user_email: userEmail,
        question_text: currentQuestion
      });
      
      setQuestionId(response.data.id);
      setQuestionData(response.data);
      setCurrentQuestion('');
      toast.success('Question submitted! Our AI is generating an answer...');
    } catch (error) {
      console.error('Error submitting question:', error);
      toast.error('Failed to submit question. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAskAnother = () => {
    setQuestionId(null);
    setQuestionData(null);
    setCurrentQuestion('');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'processing':
        return <Badge data-testid="status-processing" className="bg-blue-100 text-blue-700 hover:bg-blue-100"><Loader2 className="w-3 h-3 mr-1 animate-spin" />AI Thinking...</Badge>;
      case 'pending_review':
        return <Badge data-testid="status-pending" className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Clock className="w-3 h-3 mr-1" />Under Expert Review</Badge>;
      case 'answered':
        return <Badge data-testid="status-answered" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle className="w-3 h-3 mr-1" />Reviewed & Ready</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
              src="/procureask-logo.png" 
              alt="ProcureAsk - Procurement Simplified" 
              className="h-12 md:h-14 w-auto"
              data-testid="procureask-logo"
            />
          </div>
          <a 
            href="/admin" 
            data-testid="admin-link"
            className="text-sm text-slate-600 hover:text-primary transition-colors"
          >
            Admin
          </a>
        </div>
      </header>

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div data-testid="install-banner" className="bg-primary text-white py-3 px-4 shadow-md animate-in slide-in-from-top duration-300">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Download className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">
                <span className="font-medium">Install ProcureAsk</span> on your device for quick access!
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="install-pwa-btn"
                size="sm"
                variant="secondary"
                onClick={handleInstallClick}
                className="bg-white text-primary hover:bg-slate-100"
              >
                Install
              </Button>
              <Button
                data-testid="dismiss-install-btn"
                size="sm"
                variant="ghost"
                onClick={handleDismissInstall}
                className="text-white hover:bg-white/10"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {showIOSBanner && (
        <div data-testid="ios-install-banner" className="bg-primary text-white py-3 px-4 shadow-md animate-in slide-in-from-top duration-300">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Share className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">
                <span className="font-medium">Install ProcureAsk:</span> tap the Share icon, then "Add to Home Screen".
              </p>
            </div>
            <Button
              data-testid="dismiss-ios-install-btn"
              size="sm"
              variant="ghost"
              onClick={handleDismissIOS}
              className="text-white hover:bg-white/10"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        {step === 'intro' && (
          <div className="text-center space-y-8 animate-in fade-in zoom-in-95 duration-500">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold text-primary tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Expert Procurement Advice,<br />On Demand
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
                Get AI-powered procurement insights, reviewed and enhanced by consultancy expertise.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                data-testid="get-started-btn"
                onClick={handleStartChat}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg rounded-md shadow-md hover:shadow-lg transition-all"
              >
                Ask a Question
                <MessageSquare className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Features */}
            <div className="grid md:grid-cols-3 gap-6 mt-16">
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>AI-Powered Insights</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Instant answers powered by advanced AI technology</p>
              </div>
              
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Expert Review</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Every answer reviewed by experienced consultants</p>
              </div>
              
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Fast Response</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Get comprehensive answers quickly</p>
              </div>
            </div>
          </div>
        )}

        {step === 'form' && (
          <div className="max-w-md mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-semibold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>Let's Get Started</h2>
              <p className="text-slate-600">We'll need a few details to personalize your experience</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Your Name</label>
                <Input
                  data-testid="user-name-input"
                  type="text"
                  placeholder="John Smith"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Email Address</label>
                <Input
                  data-testid="user-email-input"
                  type="email"
                  placeholder="john@company.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="w-full"
                />
              </div>

              <Button 
                data-testid="continue-to-chat-btn"
                onClick={handleSubmitForm}
                className="w-full bg-primary hover:bg-primary/90 text-white rounded-md py-5"
              >
                Continue to Chat
              </Button>
            </div>
          </div>
        )}

        {step === 'chat' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-semibold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>Ask Your Question</h2>
              <p className="text-slate-600">Our AI will generate an answer, reviewed by our expert team</p>
            </div>

            {/* Question Display */}
            {questionData && (
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-slate-700">Your Question</span>
                      {getStatusBadge(questionData.status)}
                    </div>
                    <p data-testid="question-text" className="text-slate-900 leading-relaxed">{questionData.question_text}</p>
                  </div>
                </div>

                {/* AI Answer */}
                {questionData.ai_answer && questionData.status === 'pending_review' && (
                  <div className="border-t border-slate-200 pt-6">
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-900">Under Expert Review</span>
                      </div>
                      <p className="text-sm text-amber-700">Our procurement expert is reviewing and enhancing this answer. You'll be notified when ready.</p>
                    </div>
                  </div>
                )}

                {/* Reviewed Answer */}
                {questionData.status === 'answered' && questionData.reviewed_answer && (
                  <div className="border-t border-slate-200 pt-6">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-900">Expert-Reviewed Answer</span>
                    </div>
                    <div data-testid="reviewed-answer" className="prose prose-slate max-w-none">
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{questionData.reviewed_answer}</p>
                    </div>
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-4">Reviewed by Andy Wood, Lead Procurement Consultant</p>
                      <Button 
                        data-testid="ask-another-btn"
                        onClick={handleAskAnother}
                        variant="outline"
                        className="w-full sm:w-auto"
                      >
                        Ask Another Question
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Question Input */}
            {!questionData && (
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-4">
                <Textarea
                  data-testid="question-input"
                  placeholder="What would you like to know about procurement? (e.g., How do I evaluate suppliers for a new category?)"
                  value={currentQuestion}
                  onChange={(e) => setCurrentQuestion(e.target.value)}
                  className="min-h-32 resize-none"
                  disabled={loading}
                />
                <div className="flex justify-end">
                  <Button 
                    data-testid="submit-question-btn"
                    onClick={handleSubmitQuestion}
                    disabled={loading}
                    className="bg-primary hover:bg-primary/90 text-white rounded-md px-6"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Question
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Important Notice Section */}
      <section id="important-notice" className="mt-16 py-12 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Info className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Important Notice
            </h2>
          </div>
          <div className="bg-white border-l-4 border-blue-500 border-t border-r border-b border-slate-200 rounded-lg p-6 md:p-8 shadow-sm space-y-4 text-slate-700 leading-relaxed">
            <p>
              ProcureAsk is a domain-specific AI workflow designed exclusively for procurement-related questions, insights, and workflows. It is intended for use by procurement professionals and individuals seeking information relevant to procurement, sourcing, supplier management, contracting, purchasing, category management, and related business functions.
            </p>
            <p>
              The platform is not intended for general-purpose use or for inquiries unrelated to procurement. FlexProc reserves the right, at its sole discretion, to decline, restrict, or refuse to respond to any question or request that falls outside the intended scope of the platform, is inappropriate, or otherwise does not align with FlexProc's policies or objectives.
            </p>
            <p>
              Responses generated by ProcureAsk are provided for informational purposes only and should not be relied upon as legal, financial, regulatory, or professional advice. Users remain responsible for exercising independent judgment and verifying information before making business decisions.
            </p>
            <p className="font-medium text-slate-900">
              By using this platform, you acknowledge and agree that FlexProc retains final authority regarding whether and how any question will be addressed.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-sm text-slate-600">
            © 2026 ProcureAsk <span className="text-slate-400">by FlexProc</span>. Technology-enabled procurement for smarter decisions.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Contact: <a href="mailto:info@flexproc.co.uk" className="text-accent hover:underline">info@flexproc.co.uk</a>
            {' | '}
            <a href="#important-notice" className="text-accent hover:underline">Important Notice</a>
          </p>
        </div>
      </footer>
    </div>
  );
}